import {
  type GetSessionOpportunityArgs,
  type GetSessionPresentersArgs,
  type SessionsOpportunity,
  useInfiniteScrollFromCache,
  useLazyGetSessionsOpportunitiesQuery,
} from '@carboncopyco/data';
import type { ResourceType } from '@carboncopyco/types';
import type { DropdownFilterItemContent } from '@carboncopyco/ui';
import { getTimezoneName } from '@carboncopyco/utils';
import { useEffect, useMemo, useState } from 'react';
import { useUrlState } from '../../utils';
import type { UrlState } from '../types';
import { SessionsBreakdownFilter } from './SessionsBreakdownFilter';

type OpportunitiesFilterProps = {
  organizationId: string;
  workspaceId: string;
} & (
  | { playbookId?: never; demoId: string }
  | { playbookId: string; demoId?: never }
);

function getResourceTypes(demoId: string, tab: string): ResourceType[] {
  if (demoId) {
    return tab === 'visits'
      ? ['shared_presentation']
      : ['managed_presentation'];
  }
  return ['play'];
}

export const OpportunitiesFilter = ({
  organizationId,
  workspaceId,
  demoId,
  playbookId,
}: OpportunitiesFilterProps) => {
  const [query, setQuery] = useState('');
  const [urlState] = useUrlState<UrlState>({});
  const tab = urlState.tab;
  const resourceTypes = getResourceTypes(demoId, tab);

  const fetchArgs = useMemo<GetSessionPresentersArgs>(
    () => ({
      organizationId,
      shortSessions: true,
      internalSessions: true,
      workspaceIds: [workspaceId],
      timezone: getTimezoneName(),
      resourceTypes,
      ...(demoId ? { demoId } : { playbookId }),
      query,
    }),
    [organizationId, workspaceId, tab, demoId, query]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    fetchFirstBatch,
    fetchNextBatch,
    isEmpty,
  } = useInfiniteScrollFromCache<
    SessionsOpportunity,
    GetSessionOpportunityArgs
  >(useLazyGetSessionsOpportunitiesQuery, fetchArgs);

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs]);

  const options: DropdownFilterItemContent<string>[] =
    items?.map((item) => ({
      ...item,
      id: item.name,
      label: item.name,
    })) ?? [];

  return (
    <SessionsBreakdownFilter
      type="opportunities"
      options={options}
      query={query}
      setQuery={setQuery}
      placeholder="Opportunities"
      isLoading={isLoading}
      isLoadingBatch={isLoadingBatch}
      fetchNextBatch={fetchNextBatch}
      disabled={isEmpty}
    />
  );
};
